import React, { useEffect, useState } from 'react';
import {
  PRIORITIES, addDays, relativeDay, fmtDuration, ageInDays, uid,
} from '../myDayUtils';

const ESTIMATES = [5, 15, 30, 45, 60, 90, 120, 240];

/** Slide-over panel with everything a single activity can carry. */
export default function TaskEditor({ task, cats, today, onSave, onClose, onDelete, onMakeRoutine }) {
  const [draft, setDraft] = useState(task);
  const [newSub, setNewSub] = useState('');

  useEffect(() => { setDraft(task); setNewSub(''); }, [task]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!draft) return null;
  const set = patch => setDraft(d => ({ ...d, ...patch }));
  const commit = () => { onSave(draft); onClose(); };
  const age = ageInDays(draft, today);

  const setSub = (id, patch) =>
    set({ subtasks: draft.subtasks.map(s => (s.id === id ? { ...s, ...patch } : s)) });

  const addSub = e => {
    e.preventDefault();
    const title = newSub.trim();
    if (!title) return;
    set({ subtasks: [...(draft.subtasks || []), { id: uid(), title, done: false }] });
    setNewSub('');
  };

  const toggleTag = raw => {
    const tag = raw.trim().replace(/^#/, '').toLowerCase();
    if (!tag) return;
    const tags = draft.tags || [];
    set({ tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag] });
  };

  return (
    <>
      <div className="md-scrim" onClick={commit} aria-hidden="true" />
      <aside className="md-panel" role="dialog" aria-label="Activity details">
        <header className="md-panel__head">
          <h3 className="md-panel__title">Activity details</h3>
          <button className="md-iconbtn" onClick={commit} aria-label="Close">✕</button>
        </header>

        <div className="md-panel__body">
          <label className="md-field">
            <span className="md-field__lbl">What needs to be done</span>
            <textarea
              className="md-input md-input--title"
              value={draft.title}
              rows={2}
              autoFocus
              onChange={e => set({ title: e.target.value })}
            />
          </label>

          <div className="md-field">
            <span className="md-field__lbl">Priority</span>
            <div className="md-seg">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  className={`md-seg__btn${draft.priority === p.value ? ' md-seg__btn--on' : ''}`}
                  style={draft.priority === p.value ? { color: p.color, borderColor: p.color } : undefined}
                  onClick={() => set({ priority: p.value })}
                >{p.label}</button>
              ))}
            </div>
          </div>

          <div className="md-field md-field--row">
            <label className="md-field md-field--grow">
              <span className="md-field__lbl">Day</span>
              <input type="date" className="md-input" value={draft.date}
                onChange={e => set({ date: e.target.value || today })} />
            </label>
            <label className="md-field md-field--grow">
              <span className="md-field__lbl">Start time (optional)</span>
              <input type="time" className="md-input" value={draft.startTime || ''}
                onChange={e => set({ startTime: e.target.value || null })} />
            </label>
          </div>

          <div className="md-quickdates">
            {[['Today', today], ['Tomorrow', addDays(today, 1)], ['In 3 days', addDays(today, 3)], ['Next week', addDays(today, 7)]]
              .map(([label, date]) => (
                <button key={label} className={`md-chip md-chip--pick${draft.date === date ? ' md-chip--on' : ''}`}
                  onClick={() => set({ date })}>{label}</button>
              ))}
          </div>

          <div className="md-field">
            <span className="md-field__lbl">How long will it take</span>
            <div className="md-quickdates">
              {ESTIMATES.map(m => (
                <button key={m} className={`md-chip md-chip--pick${draft.estimateMin === m ? ' md-chip--on' : ''}`}
                  onClick={() => set({ estimateMin: draft.estimateMin === m ? null : m })}>{fmtDuration(m)}</button>
              ))}
            </div>
          </div>

          <div className="md-field">
            <span className="md-field__lbl">Category</span>
            <div className="md-quickdates">
              {cats.map(c => (
                <button key={c.id}
                  className={`md-chip md-chip--pick${draft.category === c.id ? ' md-chip--on' : ''}`}
                  style={draft.category === c.id ? { color: c.color, borderColor: c.color } : undefined}
                  onClick={() => set({ category: draft.category === c.id ? null : c.id })}>
                  {c.icon} {c.name}
                </button>
              ))}
            </div>
          </div>

          <label className="md-field">
            <span className="md-field__lbl">Tags — comma separated</span>
            <input
              className="md-input"
              defaultValue={(draft.tags || []).join(', ')}
              placeholder="prep, urgent, vendor"
              onBlur={e => set({
                tags: e.target.value.split(',').map(t => t.trim().replace(/^#/, '').toLowerCase()).filter(Boolean),
              })}
            />
            {(draft.tags || []).length > 0 && (
              <span className="md-quickdates" style={{ marginTop: 6 }}>
                {draft.tags.map(t => (
                  <button key={t} className="md-chip md-chip--tag" onClick={() => toggleTag(t)} title="Remove tag">
                    #{t} ✕
                  </button>
                ))}
              </span>
            )}
          </label>

          <div className="md-field">
            <span className="md-field__lbl">Checklist</span>
            <ul className="md-sublist">
              {(draft.subtasks || []).map(s => (
                <li key={s.id} className={`md-sub${s.done ? ' md-sub--done' : ''}`}>
                  <button className={`md-check md-check--sm${s.done ? ' md-check--on' : ''}`}
                    onClick={() => setSub(s.id, { done: !s.done })} aria-label={`Toggle ${s.title}`}>
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                  <input className="md-sub__input" value={s.title}
                    onChange={e => setSub(s.id, { title: e.target.value })} />
                  <button className="md-iconbtn md-iconbtn--danger"
                    onClick={() => set({ subtasks: draft.subtasks.filter(x => x.id !== s.id) })}
                    aria-label="Remove step">✕</button>
                </li>
              ))}
            </ul>
            <form className="md-sub__add" onSubmit={addSub}>
              <input className="md-input md-input--sm" value={newSub} placeholder="Add a step…"
                onChange={e => setNewSub(e.target.value)} />
              <button className="md-btn md-btn--sm" type="submit" disabled={!newSub.trim()}>Add step</button>
            </form>
          </div>

          <label className="md-field">
            <span className="md-field__lbl">Notes</span>
            <textarea className="md-input" rows={4} value={draft.notes || ''}
              placeholder="Vendor numbers, quantities, anything you'll want in hand."
              onChange={e => set({ notes: e.target.value })} />
          </label>

          <div className="md-field">
            <span className="md-field__lbl">Options</span>
            <label className="md-toggle">
              <input type="checkbox" checked={!!draft.important}
                onChange={e => set({ important: e.target.checked })} />
              <span>Important — moves it up the Eisenhower matrix</span>
            </label>
            <label className="md-toggle">
              <input type="checkbox" checked={!!draft.pinned}
                onChange={e => set({ pinned: e.target.checked })} />
              <span>Pin to the top of the day</span>
            </label>
            <label className="md-toggle">
              <input type="checkbox" checked={!draft.noCarry}
                onChange={e => set({ noCarry: !e.target.checked })} />
              <span>Carry forward if it doesn't get done</span>
            </label>
          </div>

          {(draft.carryCount > 0 || draft.focusSec > 0) && (
            <div className="md-panel__stats">
              {draft.carryCount > 0 && (
                <span>↪ Carried forward {draft.carryCount}× · open for {age} day{age === 1 ? '' : 's'} since {relativeDay(draft.originalDate, today)}</span>
              )}
              {draft.focusSec > 0 && <span>🎯 {fmtDuration(Math.round(draft.focusSec / 60))} of focus logged</span>}
            </div>
          )}
        </div>

        <footer className="md-panel__foot">
          <button className="md-btn md-btn--danger" onClick={() => { onDelete(draft); onClose(); }}>Delete</button>
          {!draft.routineId && (
            <button className="md-btn" onClick={() => onMakeRoutine(draft)} title="Repeat this on a schedule">
              🔁 Make it a routine
            </button>
          )}
          <button className="md-btn md-btn--primary" onClick={commit}>Save</button>
        </footer>
      </aside>
    </>
  );
}
